/**
 * Who is signed in. The last answer is remembered on the device, so the app opens without a
 * connection. When the server says a session has ended while the app is in use, a sign-in sheet
 * covers the page and everything under it stays as it was.
 */

import type { Me } from '@mawkeb/shared/api';
import { create } from 'zustand';

const KEY = 'venue.me';

function remembered(): Me | null {
  try {
    const raw = localStorage.getItem(KEY);
    const me = raw ? (JSON.parse(raw) as Me) : null;
    return me && typeof me.email === 'string' && (me.role === 'admin' || me.role === 'lead') ? me : null;
  } catch {
    return null;
  }
}

interface SessionState {
  me: Me | null;
  /** unknown until the server (or the device, offline) has answered. */
  status: 'unknown' | 'in' | 'out';
  /** The session ended while the app was open: sign in again over the page. */
  ended: boolean;
  /** Things to do again once signed in (a save that was refused for want of a session). */
  waiting: (() => void)[];
  signedIn: (me: Me) => void;
  signedOut: () => void;
  end: () => void;
  afterSignIn: (work: () => void) => void;
}

export const useSession = create<SessionState>()((set, get) => ({
  me: remembered(),
  status: 'unknown',
  ended: false,
  waiting: [],

  signedIn: (me) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(me));
    } catch {
      /* private mode */
    }
    const waiting = get().waiting;
    set({ me, status: 'in', ended: false, waiting: [] });
    for (const work of waiting) work();
  },

  signedOut: () => {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* private mode */
    }
    set({ me: null, status: 'out', ended: false, waiting: [] });
  },

  end: () => {
    if (get().status === 'in') set({ ended: true });
    else set({ status: 'out' });
  },

  afterSignIn: (work) => set({ waiting: [...get().waiting, work] }),
}));

export const session = useSession.getState;
